import { Injectable } from '@angular/core'
import { AngularFireDatabase } from 'angularfire2/database'

import { Observable, Observer } from 'rxjs/Rx'
import 'rxjs/add/operator/switchMap'

import { User } from '../../shared/user.model'
import { Movie } from '../../movies/shared/movie.model'
import { ReportOption } from '../../profile/shared/report-option.enum'
import { FacebookProviderData } from '../../auth/facebook-provider-data.model'

import { environment } from '../../../environments/environment'

import * as firebase from 'firebase'
import * as GeoFire from 'geofire'

export interface UserLocation {
  user: any
  location: number[],
  distance: number
}

@Injectable()
export class UserService {

  private readonly firebaseRef = firebase.database().ref(environment.firebaseRoot)
  private readonly geoFire = new GeoFire(this.firebaseRef.child('geofire'))

  constructor(private db: AngularFireDatabase) { }

  getUser(uid: string): Observable<User> {
    const userRef = this.firebaseRef.child(`/users/${uid}`)
    return this.db.object(userRef)
      .map((item: any) => this.mapToUser(item))
  }

  getProximityUsers(latlng: number[], radius: number, excludeKeys: string[] = []): Observable<UserLocation[]> {
      const geoQuery = this.geoFire.query({
      center: latlng,
      radius: radius
    })

    const users: UserLocation[] = []

    return Observable.create((observer: Observer<UserLocation[]>) => {
      geoQuery.on('key_entered', (key: string, location: number[], distance: number) => {
        if (excludeKeys.indexOf(key) > -1) {
          return
        }

        // console.log(key, location, distance)
        const found = users.find((userLocation: UserLocation) => userLocation.user.$key === key)
        if (found) {
          return
        }

        this.getUser(key)
          .subscribe(user => {
            const userLocation = {
              user: user,
              location: location,
              distance: distance
            }

            users.push(userLocation)
            users.sort((a: UserLocation, b: UserLocation) => {
              return a.distance - b.distance
            })

            observer.next(users)
          })
      })

      geoQuery.on('key_exited', (key: string, location: number[], distance: number) => {
        const index = users.findIndex((userLocation: UserLocation) => {
          return userLocation.user.$key === key
        })

        if (index > -1) {
          users.splice(index, 1)
        }

        observer.next(users)
      })
    })
  }

  getInterestedUsers(uid: string, excludeKeys: string[] = []): Observable<any> {
    const interestsRef = this.firebaseRef.child(`/users/${uid}/interestedMovies`)
    return this.db.list(interestsRef)
      .flatMap(results => {
        const observableBatch: Observable<any>[] = []

        for (const result of results) {
          const movieId: number = result.$key
          const interestedUsersRef = this.firebaseRef.child(`/movies/${movieId}/interestedUsers`)
          const observable = this.db.list(interestedUsersRef).first()
          observableBatch.push(observable)
        }

        return (observableBatch.length > 0)
          ? Observable.forkJoin(observableBatch)
          : Observable.of([])
      })
      .flatMap(results => {
        const users: string[] = []
        const observableBatch: Observable<any>[] = []

        for (const result of results) {
          for (const user of result) {
            const uid = user.$key
            if (users.indexOf(uid) === -1) {
              users.push(uid)
            }
          }
        }

        for (const uid of users) {
          if (excludeKeys.indexOf(uid) === -1) {
            const userRef = this.firebaseRef.child(`/users/${uid}`)
            const observable = this.db.object(userRef).first()
            observableBatch.push(observable)
          }
        }

        return (observableBatch.length > 0)
          ? Observable.forkJoin(observableBatch)
          : Observable.of([])
      })
      .map(results => {
        const users: User[] = []

        for (const result of results) {
          const user = new User()
          user.uid = result.$key
          user.images = result.images
          user.firstname = result.firstName
          user.lastname = result.lastName
          user.email = result.email

          users.push(user)
        }

        return users
      })
  }

  // getInterestedMovies(uid: string): Observable<string[]> {
  //     return this.db
  //         .list(`/users/${uid}/interestedMovies`)
  //         .map((interests: any[]) => {
  //            return interests.map(interest => interest.$key)
  //         })
  // }

  getInterestedMovies(uid: string): Observable<Movie[]> {
    const moviesRef = this.firebaseRef.child(`/users/${uid}/interestedMovies`)
    return this.db.list(moviesRef)
      .flatMap(results => {
        const observableBatch: Observable<any>[] = []

        for (const result of results) {
          if (result.$value) {
            const movieId = result.$key
            const movieRef = this.firebaseRef.child(`/movies/${movieId}`)
            const observable = this.db.object(movieRef).first()
            observableBatch.push(observable)
          }
        }

        return Observable.forkJoin(observableBatch)
      })
      .map(results => {
        const movies: Movie[] = []
        for (const result of results) {
          const movie = new Movie()
          movie.rootId = result.$key
          movie.titles = result.title
          movie.bannerUrls = result.banner
          movie.rating = result.rating
          movie.duration = result.runtime

          movies.push(movie)
        }
        movies.sort((a: Movie, b: Movie) => {
          const titleA = a.getTitle('en').toLowerCase()
          const titleB = b.getTitle('en').toLowerCase()
          return (titleA < titleB) ? -1 : (titleA > titleB) ? 1 : 0
        })
        return movies
      })
  }

  addMovieToInterests(uid: string, movie: Movie): Promise<any> {
    const updates = {}

    updates[`/users/${uid}/interestedMovies/${movie.rootId}`] = true

    updates[`/movies/${movie.rootId}/banner`] = movie.bannerUrls
    updates[`/movies/${movie.rootId}/title`] = movie.titles
    updates[`/movies/${movie.rootId}/rating`] = movie.rating
    updates[`/movies/${movie.rootId}/runtime`] = movie.duration

    updates[`/movies/${movie.rootId}/interestedUsers/${uid}`] = true

    return <Promise<any>>this.firebaseRef.update(updates)
  }

  removeMovieFromInterests(uid: string, movie: Movie): Promise<any> {
    const updates = {}
    updates[`/users/${uid}/interestedMovies/${movie.rootId}`] = null
    updates[`/movies/${movie.rootId}/interestedUsers/${uid}`] = null

    return <Promise<any>>this.firebaseRef.update(updates)
  }

  updateProfile(data: FacebookProviderData): Promise<any> {
    const uid = data.uid
    return this.createAccountIfNotExists(data)
      .then(() => {
        const updates = {}
        updates[`/users/${uid}/email`] = data.email
        updates[`/users/${uid}/firstName`] = data.firstname
        updates[`/users/${uid}/lastName`] = data.lastname
        updates[`/users/${uid}/sex`] = data.gender
        updates[`/users/${uid}/birthday`] = data.birthday
        updates[`/users/${uid}/preferences`] = {
          female: true,
          male: true,
          follow: true,
          radius: 101,
          maxAge: 100,
          minAge: 18,
        }


        return this.firebaseRef.update(updates)
      })
  }

  reportUser(uid: string, targetUid: string, reportOption: ReportOption, text?: string) {
    const updates = {}
    updates[`reportedUsers/${targetUid}/${uid}`] = {
      reason: ReportOption.toString(reportOption),
      timestamp: firebase.database.ServerValue.TIMESTAMP,
      detailedText: text || null
    }

    this.firebaseRef.update(updates)
  }

  setLocation(uid: string, latitude: string, longitude: string): Promise<any> {
    return this.geoFire.set(uid, [latitude, longitude])
  }

  private createAccountIfNotExists(data: FacebookProviderData): Promise<any> {
    const uid = data.uid

    return <Promise<any>>this.firebaseRef.child(`/users/${uid}`).once('value')
      .then((snapshot: firebase.database.DataSnapshot) => {
        if (!snapshot.exists()) {
          return this.firebaseRef.child(`users/${uid}`).set({
            firstName: data.firstname,
            lastName: data.lastname,
            email: data.email,
            sex: data.gender,
            birthday: data.birthday,
            images: [data.imageUrl],
            credits: 100,
            notifyCount: 0
          })
        } else {
          return Promise.resolve()
        }
      })
  }

  private mapToUser(item: any): User {
    const user = new User()
    user.uid = item.$key
    user.email = item.email
    user.firstname = item.firstName
    user.lastname = item.lastName
    user.credits = item.credits
    user.images = item.images

    return user
  }
}



// WEBPACK FOOTER //
// C:/Users/florian.gadal/Workspace/cinenow-web/src/app/core/users/user.service.ts